import React, { useLayoutEffect } from "react";
import HubspotForm from "react-hubspot-form";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import RichText from "@src/components/elements/RichText";
import { buildThemeClasses, getChildThemeForDuplex } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const { HUBSPOT_PORTAL_ID = "2963875" } = process.env;

function CTAArrow( { fill = "#006B3B" }) {
  return (
    <svg
      fill="none"
      height="18"
      viewBox="0 0 18 18"
      width="18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="white" fillOpacity="0.01" height="18" width="18" />
      <path
        clipRule="evenodd"
        d="M9.77613 3.77584C10.0104 3.54152 10.3903 3.54152 10.6247 3.77584L15.4247 8.57584C15.659 8.81015 15.659 9.19005 15.4247 9.42436L10.6247 14.2244C10.3903 14.4587 10.0104 14.4587 9.77613 14.2244C9.54181 13.9901 9.54181 13.6101 9.77613 13.3758L13.5519 9.6001H3.00039C2.66902 9.6001 2.40039 9.33147 2.40039 9.0001C2.40039 8.66873 2.66902 8.4001 3.00039 8.4001H13.5519L9.77613 4.62436C9.54181 4.39005 9.54181 4.01015 9.77613 3.77584Z"
        fill={fill}
        fillOpacity="0.906"
        fillRule="evenodd"
      />
    </svg>
  );
}

function Wrapper({ isInsideTabs = false, isForm = false, id, children, theme }) {
  if (isInsideTabs) {
    return children;
  }

  return (
    <section
      className={clsx(
        "SectionDuplex p-section font-atkinson",
        isForm && "relative md:scroll-mt-56",
        {...buildThemeClasses(theme)}
      )}
      id={id}
    >
      <IdTooltip id={id} />
      <div className="inner-small">{children}</div>
    </section>
  );
}

export function SectionDuplex({ entry: rawEntry, isInsideTabs = false }) {
  const entry = useLiveUpdates(rawEntry);
  const duplexPreviewProps = getContentfulPreviewPropsFn(entry.contentful_id);
  const overline = entry.compactHeading;
  const {formText} = entry;
  const icon = entry.icon ? `https:${entry.icon?.file?.url}` : null;
  const content = !entry.content?.json
    ? entry.content
    : { raw: JSON.stringify(entry.content?.json) };
  const {cta} = entry;
  const {referenceElement} = entry;
  const isForm = referenceElement?.__typename === "ContentfulForm";
  const isMedia = referenceElement?.__typename === "ContentfulItemMedia";
  const isReversed = entry.presentation === "content-second";

  // Needed due to the hubspot bug not calculating the height correctly
  useLayoutEffect(() => {
    const timer = isForm ? setTimeout(() => {
      const iframe = document.querySelector('.SectionDuplex iframe') as HTMLElement;
      if (iframe) {
        iframe.style.height = '587px'; // random close to good number, after which form will recalculate its height.
      }
    }, 1000) : undefined;

    return () => {
      clearTimeout(timer);
    };
  }, [isForm]);

  return (
    <Wrapper
      id={entry.contentful_id}
      isForm={isForm}
      isInsideTabs={isInsideTabs}
      theme={entry.customTheme}
    >
      <div
        className={clsx(
          "flex gap-8 flex-col md:flex-row animate-fadeIn",
          isReversed && "md:flex-row-reverse",
          isForm ? "items-start" : "items-center",
          isInsideTabs && "p-4 bg-white border border-neutral-6-alpha rounded-lg md:p-0 md:bg-transparent md:border-0 md:rounded-none",
          isInsideTabs && buildThemeClasses(getChildThemeForDuplex(entry.customTheme)),
          isInsideTabs && 'md:theme--none'
        )}
        {...duplexPreviewProps("tabs")}
      >
        <div
          className={clsx(
            "flex flex-col w-full md:w-6/12 items-start",
            isInsideTabs ? "gap-5" : "gap-3"
          )}
        >
          {overline && !isInsideTabs && (
            <p
              className={clsx(
                "uppercase",
              )}
              {...duplexPreviewProps("compactHeading")}
            >
              {overline}
            </p>
          )}

          {icon && isInsideTabs && (
            <div
              className="w-48px h-48px rounded flex items-center justify-center"
              {...getContentfulPreviewPropsFn(entry.contentful_id)("icon")}
            >
              {!entry.icon?.big && (
                <img alt="" aria-hidden height="48" src={icon} width="48" />
              )}
              {entry.icon?.big && (
                <GatsbyImage
                  alt=""
                  aria-hidden
                  image={getImage(entry.icon?.big)}
                  {...duplexPreviewProps("icon")}
                />
              )}
            </div>
          )}
          {content && (
            <div {...duplexPreviewProps("content")}>
              <RichText
                bodyRichText={content}
                classes="space-y-4 duplex-fix"
                compact={isInsideTabs}
                useHeadings
              />
            </div>
          )}
          {cta?.length < 2 && (
            <a
              className="btn-new group"
              href={cta.at(0).url}
              {...getContentfulPreviewPropsFn(cta.at(0).contentful_id)("label")}
            >
              {cta.at(0).label}
              <CTAArrow fill="currentColor" />
            </a>
          )}

          {cta?.length > 1 && (
            <div className="flex gap-2 mt-3" {...duplexPreviewProps("cta")}>
              {cta?.map((item) => (
                <a key={item.label} className="btn-new" href={item.url}>
                  <span
                    {...getContentfulPreviewPropsFn(item.contentful_id)(
                      "label"
                    )}
                  >
                    {item.label}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
        <div
          className={clsx(
            (isForm || isMedia) && "w-full md:w-6/12",
            isForm && !isInsideTabs && "md:-mt-32"
          )}
        >
          <div
            className={clsx(`w-full`, isForm ? "" : "max-h-320px")}
            {...duplexPreviewProps("referenceElement")}
          >
            {isMedia && !referenceElement?.media?.gatsbyImageData && (
              <div className="flex max-h-320px justify-center">
                <img
                  alt={referenceElement?.media?.title}
                  className="max-w-full max-h-full"
                  src={`https:${referenceElement?.media?.file?.url}`}
                />
              </div>
            )}
            {isMedia && referenceElement?.media?.gatsbyImageData && (
              <GatsbyImage
                alt={referenceElement?.media?.title}
                image={getImage(referenceElement?.media?.gatsbyImageData)}
              />
            )}
            {isForm && (
              <div className="px-4 py-5 bg-rt-white rounded-lg border border-neutral-6-alpha md:px-10 md:py-16">
                {referenceElement.subtitle && (
                  <p className="text-accent-10-alpha uppercase mb-2">
                    {referenceElement.subtitle}
                  </p>
                )}

                {referenceElement.title && (
                  <p className="mt-2 mb-8 text-rt-2m text-black">
                    {referenceElement.title}
                  </p>
                )}

                <HubspotForm
                  formId={referenceElement?.hubSpotFormId}
                  loading={<div>Loading...</div>}
                  portalId={HUBSPOT_PORTAL_ID}
                />
              </div>
            )}
          </div>

          {formText && (
            <p className="text-rt-s text-neutral-10-alpha mt-5">{formText}</p>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

export const SectionDuplexFragment = graphql`
  fragment SectionDuplex on ContentfulSectionDuplex {
    contentful_id
    __typename
    sys {
      id
    }
    customTheme
    compactHeading
    icon {
      ...SectionDuplexIcon
    }
    presentation
    content {
      ...SectionDuplexContent
    }
    cta {
      ...SectionDuplexCTA
    }
    referenceElement {
      ...SectionDuplexForm
      ...SectionDuplexItemMedia
    }
  }
`;

export const SectionDuplexIcon = graphql`
  fragment SectionDuplexIcon on ContentfulAsset {
    title
    file {
      url
      fileName
      contentType
    }
    small: gatsbyImageData(
      placeholder: BLURRED
      height: 32
      width: 32
      resizingBehavior: PAD
    )
    big: gatsbyImageData(
      placeholder: BLURRED
      height: 48
      width: 48
      resizingBehavior: PAD
    )
  }
`;

export const SectionDuplexContent = graphql`
  fragment SectionDuplexContent on ContentfulSectionDuplexContent {
    raw
  }
`;

export const SectionDuplexCTA = graphql`
  fragment SectionDuplexCTA on ContentfulLink {
    contentful_id
    __typename
    sys {
      id
    }
    url
    label
  }
`;

export const SectionDuplexForm = graphql`
  fragment SectionDuplexForm on ContentfulForm {
    contentful_id
    __typename
    sys {
      id
    }
    moduleName
    subtitle
    title
    hubSpotFormId
  }
`;

export const SectionDuplexItemMedia = graphql`
  fragment SectionDuplexItemMedia on ContentfulItemMedia {
    contentful_id
    __typename
    sys {
      id
    }
    media {
      title
      file {
        url
        fileName
        contentType
      }
      gatsbyImageData(
        placeholder: NONE
        height: 345
        width: 510
        resizingBehavior: PAD
        quality: 100
      )
    }
  }
`;
